import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { useStaffDispatch, useStaffSelector } from 'modules/staff/store';
import {
  clearSearchLocation,
  setSearchFilter,
  setSearchQuery,
  updateSearchLocation,
} from 'modules/staff/store/ducks/search';
import {
  DisplayedType,
  EMPTY_SEARCH_LOCATION,
  listingTypesBySection,
} from 'utils/constants';
import { truncateToUTF8Bytes } from 'utils/string';
import { getFirstParam } from 'utils/url';

export function useStaffSearchMenu() {
  const location = useLocation();
  const {
    query: reduxQuery,
    type: reduxListingType,
    searchLocation: reduxSearchLocation,
  } = useStaffSelector(
    ({
      search: {
        query,
        filters: { type },
        searchLocation,
      },
    }) => ({
      query,
      type,
      searchLocation,
    }),
  );

  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const dispatch = useStaffDispatch();

  const [listingType, setListingType] = useState<StaffTypeFilter>(
    (getFirstParam(location, 'type') as StaffTypeFilter) || 'ALL',
  );
  const [query, setQuery] = useState(
    truncateToUTF8Bytes(reduxQuery || getFirstParam(location, 'q') || '', 512),
  );
  const [searchLocation, setSearchLocation] = useState<
    SearchLocation | null | undefined
  >(reduxSearchLocation);

  const [enqueueSearch, setEnqueueSearch] = useState<[boolean, string | null]>([
    false,
    null,
  ]);

  const search = useCallback(
    (searchQuery: string) => {
      if (reduxListingType !== listingType) {
        dispatch(setSearchFilter('type', listingType));
      }

      if (reduxQuery !== searchQuery) {
        dispatch(setSearchQuery(searchQuery));
      }

      if (reduxSearchLocation !== searchLocation) {
        if (searchLocation) {
          dispatch(updateSearchLocation(searchLocation));
        } else {
          dispatch(clearSearchLocation());
        }
      }

      navigate(
        `/staff${`?q=${encodeURIComponent(searchQuery)}&type=${listingType}`}`,
      );
    },
    [
      dispatch,
      listingType,
      navigate,
      reduxListingType,
      reduxQuery,
      reduxSearchLocation,
      searchLocation,
    ],
  );

  const onLocationChange = useCallback(
    (
      newSearchLocation: SearchLocation | null | undefined,
      shouldEnqueueSearch = true,
    ) => {
      setSearchLocation(newSearchLocation);

      if (!newSearchLocation) {
        // @ts-expect-error need to fix incorrect types
        updateSearchLocation(EMPTY_SEARCH_LOCATION);
      }

      if (shouldEnqueueSearch) {
        setEnqueueSearch([true, query]);
      }
    },
    [query],
  );

  useEffect(() => {
    const [enqueued, searchQuery] = enqueueSearch;

    if (enqueued) {
      setEnqueueSearch([false, null]);
      search(searchQuery || '');
    }
  }, [enqueueSearch, search]);

  useEffect(() => {
    if (navigationType === 'POP') {
      const queryParams = queryString.parse(location.search);

      if (typeof queryParams.q === 'string' && queryParams.q !== reduxQuery) {
        setQuery(truncateToUTF8Bytes(queryParams.q || '', 512));
      }
    }
  }, [location, navigationType, reduxQuery]);

  useEffect(() => {
    setSearchLocation(reduxSearchLocation);
  }, [reduxSearchLocation, setSearchLocation]);

  const listingTypes: Array<DisplayedType> = listingTypesBySection.STAFF;
  const selectedType =
    listingTypes.find((l) => l === listingType) ||
    (listingTypes.includes('JOB') ? 'JOB' : listingTypes[0]);

  const setUserQuery = useCallback(
    (q: string) => {
      // Algolia has a max query length of 512 bytes
      setQuery(truncateToUTF8Bytes(q, 512));
    },
    [setQuery],
  );

  const clearSearchInput = useCallback(() => {
    setUserQuery('');
    search('');
  }, [setUserQuery, search]);

  return {
    listingTypes,
    selectedType,
    query,
    searchLocation,
    clearSearchInput,
    search,
    setUserQuery,
    onLocationChange,
    setListingType,
  };
}
